import { useCallback } from 'react';

import { SelectionBounds } from '../utils/selectionCalculations';

/** Keyboard navigation for selection (arrows, Home/End, Space/Enter). */
export function useSelectionKeyboard(
  videoDuration: number,
  onSelectionMove?: (startTime: number) => void,
  onSeek?: (time: number) => void,
  onPlayPause?: () => void,
  containerRef?: React.RefObject<HTMLDivElement>
) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, selection: SelectionBounds) => {
      if (!onSelectionMove || videoDuration === 0) return;

      const currentStartTime = selection.startTime;
      let newStartTime = currentStartTime;
      let handled = true;

      switch (e.key) {
        case 'ArrowLeft': {
          e.preventDefault();
          const increment = e.shiftKey ? 1.0 : 0.1;
          newStartTime = Math.max(0, currentStartTime - increment);
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          const increment = e.shiftKey ? 1.0 : 0.1;
          newStartTime = Math.max(
            0,
            Math.min(
              videoDuration - selection.duration,
              currentStartTime + increment
            )
          );
          break;
        }
        case 'Home': {
          e.preventDefault();
          newStartTime = 0;
          break;
        }
        case 'End': {
          e.preventDefault();
          newStartTime = Math.max(0, videoDuration - selection.duration);
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          if (onPlayPause) {
            onPlayPause();
          }
          return;
        }
        case 'Backspace':
        case 'Delete': {
          e.preventDefault();
          newStartTime = 0;
          break;
        }
        default:
          handled = false;
      }

      if (handled && newStartTime !== currentStartTime) {
        onSelectionMove(newStartTime);

        onSeek?.(newStartTime);

        requestAnimationFrame(() => {
          // Delay to ensure DOM updates complete before re-focusing
          setTimeout(() => {
            if (containerRef?.current) {
              const selectionHandle = containerRef.current.querySelector(
                '.selection-handle'
              ) as HTMLElement;
              if (
                selectionHandle &&
                document.activeElement !== selectionHandle
              ) {
                selectionHandle.focus();
              }
            }
          }, 10);
        });
      }
    },
    [videoDuration, onSelectionMove, onSeek, onPlayPause, containerRef]
  );

  return {
    handleKeyDown,
  };
}
